import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';

export function PaidAnimation({ amount, onClose }) {
  const [checkPosition, setCheckPosition] = useState({ x: 50, y: 40 });
  const [textPosition, setTextPosition] = useState({ x: 50, y: 65 });
  const [logoPosition, setLogoPosition] = useState({ x: 50, y: 25 });
  const [checkVelocity, setCheckVelocity] = useState({ x: 2, y: 1.5 });
  const [textVelocity, setTextVelocity] = useState({ x: -1.5, y: 2 });
  const [logoVelocity, setLogoVelocity] = useState({ x: 1.2, y: -1.8 });

  useEffect(() => {
    const interval = setInterval(() => {
      // Update logo position
      setLogoPosition((prev) => {
        let newX = prev.x + logoVelocity.x;
        let newY = prev.y + logoVelocity.y;
        let newVelX = logoVelocity.x;
        let newVelY = logoVelocity.y;

        // Bounce off edges
        if (newX <= 10 || newX >= 90) {
          newVelX = -newVelX;
          newX = newX <= 10 ? 10 : 90;
        }
        if (newY <= 10 || newY >= 40) {
          newVelY = -newVelY;
          newY = newY <= 10 ? 10 : 40;
        }

        setLogoVelocity({ x: newVelX, y: newVelY });
        return { x: newX, y: newY };
      });

      // Update checkmark position
      setCheckPosition((prev) => {
        let newX = prev.x + checkVelocity.x;
        let newY = prev.y + checkVelocity.y;
        let newVelX = checkVelocity.x;
        let newVelY = checkVelocity.y;

        // Bounce off edges (with 10% padding)
        if (newX <= 10 || newX >= 90) {
          newVelX = -newVelX;
          newX = newX <= 10 ? 10 : 90;
        }
        if (newY <= 35 || newY >= 60) {
          newVelY = -newVelY;
          newY = newY <= 35 ? 35 : 60;
        }

        setCheckVelocity({ x: newVelX, y: newVelY });
        return { x: newX, y: newY };
      });

      // Update text position
      setTextPosition((prev) => {
        let newX = prev.x + textVelocity.x;
        let newY = prev.y + textVelocity.y;
        let newVelX = textVelocity.x;
        let newVelY = textVelocity.y;

        // Bounce off edges
        if (newX <= 10 || newX >= 90) {
          newVelX = -newVelX;
          newX = newX <= 10 ? 10 : 90;
        }
        if (newY <= 50 || newY >= 85) {
          newVelY = -newVelY;
          newY = newY <= 50 ? 50 : 85;
        }

        setTextVelocity({ x: newVelX, y: newVelY });
        return { x: newX, y: newY };
      });
    }, 50); // Update every 50ms for smooth animation

    return () => clearInterval(interval);
  }, [checkVelocity, textVelocity, logoVelocity]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      }}
    >
      {/* Floating PayMig Logo */}
      <div
        className="absolute transition-all duration-100 ease-linear"
        style={{
          left: `${logoPosition.x}%`,
          top: `${logoPosition.y}%`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="bg-white rounded-xl p-4 shadow-2xl">
          <img 
            src="/logo-header.png" 
            alt="PayMig" 
            className="h-12 w-auto"
          />
        </div>
      </div>

      {/* Floating Checkmark */}
      <div
        className="absolute transition-all duration-100 ease-linear"
        style={{
          left: `${checkPosition.x}%`,
          top: `${checkPosition.y}%`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="bg-white rounded-full p-6 shadow-2xl animate-pulse">
          <Check className="w-16 h-16 text-green-500" strokeWidth={3} />
        </div>
      </div>

      {/* Floating "Payment Done!" Text */}
      <div
        className="absolute transition-all duration-100 ease-linear"
        style={{
          left: `${textPosition.x}%`,
          top: `${textPosition.y}%`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="text-center space-y-2">
          <div className="text-4xl font-bold text-white drop-shadow-lg">
            Payment Done!
          </div>
          {amount && (
            <div className="text-2xl text-white/90 drop-shadow-md">
              {amount} SEK
            </div>
          )}
        </div>
      </div>

      {/* Done Button - Fixed at Lower Right */}
      <div className="absolute bottom-8 right-8">
        <Button
          onClick={onClose}
          size="lg"
          className="bg-white text-green-600 hover:bg-white/90 shadow-xl font-semibold text-lg px-8 py-6"
        >
          Done
        </Button>
      </div>
    </div>
  );
}
